/**
 * CONSAI Business App - WhatsApp Daily Summary Banner
 * Phase 4 Feature 6: Daily Sales Notification via WhatsApp
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { MessageCircle, X, TrendingUp, TrendingDown, Users, Package, Sparkles } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WhatsAppSummaryBanner = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  const isPremium = user?.current_plan === 'premium' || user?.current_plan === 'premium_trial';
  const hasWhatsAppEnabled = user?.notification_preferences?.whatsapp_notifications;

  useEffect(() => {
    if (isPremium && hasWhatsAppEnabled) {
      fetchYesterdaySummary();
    } else {
      setLoading(false);
    }
  }, [isPremium, hasWhatsAppEnabled]);

  const fetchYesterdaySummary = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ai/daily-summary`, { withCredentials: true });
      if (res.data.summary) {
        setSummary(res.data.summary);
      }
    } catch (err) {
      console.error('Failed to fetch summary for WhatsApp:', err);
    }
    setLoading(false);
  };

  // Generate WhatsApp message
  const generateWhatsAppMessage = () => {
    if (!summary) return '';
    
    const businessName = user?.business_name || 'My Business';
    const date = new Date();
    date.setDate(date.getDate() - 1);
    const dateStr = date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    
    let message = `📊 *${businessName} - Daily Summary*\n`;
    message += `📅 ${dateStr}\n\n`;
    
    message += `💰 *Sales*: ₹${(summary.sales_total || 0).toLocaleString('en-IN')} (${summary.sales_count || 0} transactions)\n`;
    
    if (summary.vs_previous !== undefined) {
      const trend = summary.vs_previous > 0 ? '📈' : summary.vs_previous < 0 ? '📉' : '➖';
      message += `${trend} *vs Yesterday*: ${summary.vs_previous > 0 ? '+' : ''}${summary.vs_previous}%\n`;
    }
    
    if (summary.new_customers > 0) {
      message += `👥 *New Customers*: ${summary.new_customers}\n`;
    }
    
    if (summary.credit_collected > 0) {
      message += `💵 *Credit Collected*: ₹${summary.credit_collected.toLocaleString('en-IN')}\n`;
    }
    
    if (summary.low_stock_count > 0) {
      message += `⚠️ *Low Stock Items*: ${summary.low_stock_count}\n`;
    }
    
    if (summary.top_selling_item && summary.top_selling_item !== 'N/A') {
      message += `🏆 *Top Seller*: ${summary.top_selling_item}\n`;
    }
    
    message += `\n_Powered by CONSAI_`;
    
    return encodeURIComponent(message);
  };

  // Open WhatsApp with pre-filled message
  const handleShareOnWhatsApp = () => {
    const message = generateWhatsAppMessage();
    const whatsappUrl = `https://wa.me/?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  // Don't show if not premium, no WhatsApp enabled, dismissed, or no summary
  if (!isPremium || !hasWhatsAppEnabled || dismissed || loading) {
    return null;
  }

  if (!summary) {
    return null;
  }

  return (
    <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200 mb-6" data-testid="whatsapp-summary-banner">
      <CardContent className="py-4 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
              <MessageCircle className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="font-medium text-green-800 font-manrope flex items-center gap-2">
                {language === 'hi' ? 'कल का सारांश WhatsApp पर शेयर करें' : "Share Yesterday's Summary on WhatsApp"}
                <Badge className="bg-green-600 text-white text-xs">
                  {summary.vs_previous > 0 ? <TrendingUp className="h-3 w-3 inline mr-1" /> : <TrendingDown className="h-3 w-3 inline mr-1" />}
                  {summary.vs_previous > 0 ? '+' : ''}{summary.vs_previous}%
                </Badge>
              </p>
              <p className="text-sm text-green-600 flex items-center gap-3">
                <span>₹{(summary.sales_total || 0).toLocaleString('en-IN')} {language === 'hi' ? 'बिक्री' : 'sales'}</span>
                {summary.new_customers > 0 && (
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" /> {summary.new_customers} {language === 'hi' ? 'नए' : 'new'}
                  </span>
                )}
                {summary.low_stock_count > 0 && (
                  <span className="flex items-center gap-1 text-amber-600">
                    <Package className="h-3 w-3" /> {summary.low_stock_count} {language === 'hi' ? 'कम स्टॉक' : 'low stock'}
                  </span>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              onClick={handleShareOnWhatsApp}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="share-whatsapp-btn"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              {language === 'hi' ? 'WhatsApp पर शेयर करें' : 'Share on WhatsApp'}
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => setDismissed(true)}
              className="text-green-600 hover:text-green-800 hover:bg-green-200"
              data-testid="dismiss-whatsapp-banner"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WhatsAppSummaryBanner;
